/**
 * Workspace cache: the normalized bootstrap snapshot kept up to date by realtime envelopes.
 * Pure reducers — the platform data layer stores the result (TanStack Query on web, GRDB/SQLDelight natively).
 *
 * Counters with their own update events are server-authoritative (Channel.memberCount via channel.updated).
 * Unread/mention state has no event, so it is derived here from message.created / read.updated.
 */
import type {
  Block,
  BootstrapResponse,
  Channel,
  ChannelMembership,
  EventEnvelope,
  Inline,
  Member,
  Message,
  Huddle,
  PersistentEvent,
  SidebarFolder,
  SidebarItem,
  UserGroup,
  UserGroupPolicy,
  Workspace,
} from "@bundle/contracts";
import { applyHuddleEvent, huddlesFromList } from "./huddle.ts";
import { groupIncludes } from "./userGroups.ts";

export type WorkspaceCache = {
  workspace: Workspace;
  meId: string;
  /** Last applied workspace event seq (hello.lastSeq on reconnect). */
  seq: number;
  members: Record<string, Member>;
  channels: Record<string, Channel>;
  /** My memberships, by channelId. */
  memberships: Record<string, ChannelMembership>;
  /** Active huddles in my conversations, by channelId (absent until there has been one; logic/huddle). */
  huddles?: Record<string, Huddle>;
  /** My sidebar folders, by id (absent until I have one; logic/sidebar). */
  sidebarFolders?: Record<string, SidebarFolder>;
  /** My sidebar placements (items in folders or ordered by hand), by item id (absent until there is one). */
  sidebarItems?: Record<string, SidebarItem>;
  /** The server can write AI thread summaries (bootstrap `aiAvailable`; logic/threadSummary). */
  aiAvailable?: boolean;
  /** Mail, Contacts and Calendar are on for this workspace (bootstrap `mail`). */
  mail?: boolean;
  /** User groups by id, disabled ones included (absent until there is one; logic/userGroups). */
  userGroups?: Record<string, UserGroup>;
  /** Who can manage user groups (absent ⇒ "everyone"). */
  userGroupPolicy?: UserGroupPolicy;
  /** People I blocked (bootstrap `blockedUserIds`, `blocks_changed`; absent until there is one; logic/moderation). */
  blockedUserIds?: string[];
};

/** "applied": state changed and seq advanced; "ignored": already applied; "gap": events were missed → resync. */
export type EnvelopeResult = "applied" | "ignored" | "gap";
export type WorkspaceUpdate = { cache: WorkspaceCache; result: EnvelopeResult };

export function fromBootstrap(b: BootstrapResponse): WorkspaceCache {
  const members: Record<string, Member> = {};
  for (const m of b.members) members[m.userId] = m;
  members[b.me.userId] = b.me;
  const channels: Record<string, Channel> = {};
  for (const c of b.channels) channels[c.id] = c;
  const memberships: Record<string, ChannelMembership> = {};
  for (const m of b.memberships) if (m.userId === b.me.userId) memberships[m.channelId] = m;
  const cache: WorkspaceCache = { workspace: b.workspace, meId: b.me.userId, seq: b.seq, members, channels, memberships };
  if (b.huddles !== undefined && b.huddles.length > 0) cache.huddles = huddlesFromList(b.huddles);
  if (b.aiAvailable === true) cache.aiAvailable = true;
  if (b.mail === true) cache.mail = true;
  if (b.userGroups !== undefined && b.userGroups.length > 0) {
    const groups: Record<string, UserGroup> = {};
    for (const g of b.userGroups) groups[g.id] = g;
    cache.userGroups = groups;
  }
  if (b.userGroupPolicy !== undefined && b.userGroupPolicy !== "everyone") cache.userGroupPolicy = b.userGroupPolicy;
  if (b.blockedUserIds !== undefined && b.blockedUserIds.length > 0) cache.blockedUserIds = [...b.blockedUserIds];
  if (b.sidebarFolders !== undefined && b.sidebarFolders.length > 0) {
    const folders: Record<string, SidebarFolder> = {};
    for (const f of b.sidebarFolders) folders[f.id] = f;
    cache.sidebarFolders = folders;
  }
  if (b.sidebarItems !== undefined && b.sidebarItems.length > 0) {
    const items: Record<string, SidebarItem> = {};
    for (const it of b.sidebarItems) items[it.id] = it;
    cache.sidebarItems = items;
  }
  return cache;
}

// ---------- Mentions ----------

function inlineMentions(nodes: Inline[], meId: string, groups: Record<string, UserGroup> | undefined): boolean {
  for (const n of nodes) {
    if (n.type === "mention_user" && n.userId === meId) return true;
    if (n.type === "mention_broadcast") return true;
    if (n.type === "mention_group" && groupIncludes(groups?.[n.groupId], meId)) return true;
  }
  return false;
}

function blocksMention(blocks: Block[], meId: string, groups: Record<string, UserGroup> | undefined): boolean {
  for (const b of blocks) {
    if (b.type === "paragraph" && inlineMentions(b.children, meId, groups)) return true;
    if (b.type === "quote" && blocksMention(b.children, meId, groups)) return true;
    if (b.type === "list") for (const item of b.items) if (blocksMention(item, meId, groups)) return true;
  }
  return false;
}

/**
 * Does this message notify me as a mention? (every message in a DM/group DM; @me, @here/@channel/@everyone or a user
 * group I'm in elsewhere)
 */
export function mentionsMe(m: Message, channel: Channel | undefined, meId: string, groups?: Record<string, UserGroup>): boolean {
  if (m.authorId === meId || m.deleted) return false;
  if (channel !== undefined && (channel.kind === "dm" || channel.kind === "group_dm")) return true;
  return blocksMention(m.blocks, meId, groups);
}

/** Shown in the channel timeline (top-level, or a thread reply also sent to the channel). */
export function isChannelLevel(m: Message): boolean {
  return m.threadRootId === undefined || m.alsoSentToChannel === true;
}

function defaultMembership(channelId: string, userId: string, lastReadSeq: number, joinedAt: number): ChannelMembership {
  return { channelId, userId, lastReadSeq, mentionCount: 0, notify: "default", muted: false, starred: false, joinedAt };
}

/** Marks a channel read up to `seq` for me (optimistic local read, or read.updated). Mentions reset when caught up. */
export function applyRead(cache: WorkspaceCache, channelId: string, seq: number): WorkspaceCache {
  const ms = cache.memberships[channelId];
  if (ms === undefined || seq <= ms.lastReadSeq) return cache;
  const channel = cache.channels[channelId];
  const caughtUp = channel === undefined || seq >= channel.lastMessageSeq;
  return {
    ...cache,
    memberships: { ...cache.memberships, [channelId]: { ...ms, lastReadSeq: seq, mentionCount: caughtUp ? 0 : ms.mentionCount } },
  };
}

/**
 * "Mark unread" from the message at `seq` (my optimistic local copy of POST …/unread): the read marker moves back to
 * seq - 1, even below where it is. Mentions stay as they are (the server doesn't restore cleared ones either).
 */
export function applyUnread(cache: WorkspaceCache, channelId: string, seq: number): WorkspaceCache {
  const ms = cache.memberships[channelId];
  const target = Math.max(0, seq - 1);
  if (ms === undefined || target === ms.lastReadSeq) return cache;
  return { ...cache, memberships: { ...cache.memberships, [channelId]: { ...ms, lastReadSeq: target } } };
}

/** Channel/unread bookkeeping for a new channel-level message (realtime or my own send response). */
export function applyNewMessage(cache: WorkspaceCache, m: Message): WorkspaceCache {
  if (!isChannelLevel(m)) return cache;
  const channel = cache.channels[m.channelId];
  let next = cache;
  if (channel !== undefined && m.seq > channel.lastMessageSeq) {
    next = { ...next, channels: { ...next.channels, [channel.id]: { ...channel, lastMessageSeq: m.seq, lastMessageAt: m.createdAt } } };
  }
  const ms = next.memberships[m.channelId];
  if (ms === undefined) return next;
  if (m.authorId === cache.meId) {
    if (m.seq <= ms.lastReadSeq && ms.mentionCount === 0) return next;
    return { ...next, memberships: { ...next.memberships, [ms.channelId]: { ...ms, lastReadSeq: Math.max(ms.lastReadSeq, m.seq), mentionCount: 0 } } };
  }
  if (m.seq > ms.lastReadSeq && (channel === undefined || m.seq > channel.lastMessageSeq) && mentionsMe(m, channel, cache.meId, cache.userGroups)) {
    return { ...next, memberships: { ...next.memberships, [ms.channelId]: { ...ms, mentionCount: ms.mentionCount + 1 } } };
  }
  return next;
}

function applyEvent(cache: WorkspaceCache, ev: PersistentEvent, ts: number): WorkspaceCache {
  switch (ev.type) {
    case "message.created":
      return applyNewMessage(cache, ev.message);
    case "message.updated":
    case "message.deleted":
    case "reaction.added":
    case "reaction.removed":
      return cache;
    case "channel.created": {
      const c = ev.channel;
      const next = { ...cache, channels: { ...cache.channels, [c.id]: c } };
      const mine = c.createdBy === cache.meId || (c.dmUserIds !== undefined && c.dmUserIds.includes(cache.meId));
      if (!mine || cache.memberships[c.id] !== undefined) return next;
      return { ...next, memberships: { ...next.memberships, [c.id]: defaultMembership(c.id, cache.meId, c.lastMessageSeq, ts) } };
    }
    case "channel.updated": {
      const c = ev.channel;
      // Made private or archived without me in it: no longer visible (bootstrap wouldn't list it either).
      if (cache.memberships[c.id] === undefined && (c.kind !== "public" || c.archived)) {
        if (cache.channels[c.id] === undefined) return cache;
        const channels = { ...cache.channels };
        delete channels[c.id];
        return { ...cache, channels };
      }
      return { ...cache, channels: { ...cache.channels, [c.id]: c } };
    }
    case "channel.member_joined": {
      if (ev.userId !== cache.meId || cache.memberships[ev.channelId] !== undefined) return cache;
      const c = cache.channels[ev.channelId];
      const ms = defaultMembership(ev.channelId, cache.meId, c === undefined ? 0 : c.lastMessageSeq, ts);
      return { ...cache, memberships: { ...cache.memberships, [ev.channelId]: ms } };
    }
    case "channel.member_left": {
      if (ev.userId !== cache.meId) return cache;
      const memberships = { ...cache.memberships };
      delete memberships[ev.channelId];
      const c = cache.channels[ev.channelId];
      if (c === undefined || c.kind === "public") return { ...cache, memberships };
      const channels = { ...cache.channels };
      delete channels[ev.channelId];
      return { ...cache, memberships, channels };
    }
    case "read.updated":
      if (ev.userId !== cache.meId) return cache;
      // "Mark unread" on another of my clients moves the marker back; plain reads only forward.
      return ev.unread === true ? applyUnread(cache, ev.channelId, ev.lastReadSeq + 1) : applyRead(cache, ev.channelId, ev.lastReadSeq);
    case "member.updated":
      return { ...cache, members: { ...cache.members, [ev.member.userId]: mergeMemberEvent(cache.members[ev.member.userId], ev.member) } };
    case "workspace.updated":
      return { ...cache, workspace: ev.workspace };
    // Docs & databases are cached by their own reducer (logic/docs).
    case "page.created":
    case "page.updated":
    case "page.removed":
    case "database.updated":
    case "row.created":
    case "row.updated":
    case "row.deleted":
    case "view.created":
    case "view.updated":
    case "view.deleted":
      return cache;
    case "membership.updated":
      return ev.membership.userId === cache.meId ? setMembership(cache, ev.membership) : cache;
    case "sidebar_item.updated":
      return setSidebarItem(cache, ev.item);
    case "sidebar_folder.updated":
      return upsertSidebarFolder(cache, ev.folder);
    case "sidebar_folder.deleted":
      return removeSidebarFolder(cache, ev.folderId);
    case "huddle.started":
    case "huddle.updated":
    case "huddle.ended": {
      const before = cache.huddles ?? {};
      const huddles = applyHuddleEvent(before, ev);
      return huddles === before ? cache : { ...cache, huddles };
    }
    // Mail lives in MailboxViewModel (online, not in the workspace cache).
    case "mail.thread.updated":
    case "mail.mailbox.updated":
    case "mail.mailbox.deleted":
    case "mail.domain.updated":
    case "calendar.event.updated":
    case "calendar.event.removed":
      return cache;
    case "user_group.updated":
      return { ...cache, userGroups: { ...cache.userGroups, [ev.group.id]: ev.group } };
    case "user_groups.policy_updated": {
      if (ev.policy === "everyone") {
        const { userGroupPolicy: _, ...rest } = cache;
        return rest;
      }
      return { ...cache, userGroupPolicy: ev.policy };
    }
    case "blocks_changed":
      return setBlockedUserIds(cache, ev.userIds);
    // Files lists refetch on their own (DriveViewModel); nothing in the workspace cache changes.
    case "drive.changed":
      return cache;
  }
}

/** Replaces my blocked list (`blocks_changed`, or an optimistic block/unblock via logic/moderation `withBlocked`). */
export function setBlockedUserIds(cache: WorkspaceCache, userIds: string[]): WorkspaceCache {
  if (userIds.length === 0) {
    if (cache.blockedUserIds === undefined) return cache;
    const { blockedUserIds: _, ...rest } = cache;
    return rest;
  }
  return { ...cache, blockedUserIds: [...userIds] };
}

/**
 * Continuity of an envelope against my last applied seq. The gateway filters events by audience, so the seqs one
 * client receives are not contiguous; each `event` frame carries `prevSeq` (the previous seq sent on that
 * connection, or the replay cursor). "ignored": already applied; "gap": something was missed in between (resync);
 * "applied": safe to apply. Without prevSeq (old gateways, REST pages applied one by one) seq must be last + 1.
 */
export function continuity(lastSeq: number, seq: number, prevSeq: number | undefined): EnvelopeResult {
  if (seq <= lastSeq) return "ignored";
  if (prevSeq === undefined) return seq === lastSeq + 1 ? "applied" : "gap";
  return prevSeq <= lastSeq ? "applied" : "gap";
}

/**
 * Applies one envelope (see `continuity`). Already-applied seqs are ignored; a gap changes nothing and reports
 * "gap" (the caller resyncs: bootstrap again and reconnect).
 */
export function applyEnvelope(cache: WorkspaceCache, env: EventEnvelope, prevSeq?: number): WorkspaceUpdate {
  const result = continuity(cache.seq, env.seq, prevSeq);
  if (result !== "applied") return { cache, result };
  return { cache: { ...applyEvent(cache, env.event, env.ts), seq: env.seq }, result: "applied" };
}

// ---------- My conversation settings and sidebar sections ----------

/** Replaces my membership of a conversation I'm in (server `membership.updated`, or an optimistic edit). */
export function setMembership(cache: WorkspaceCache, ms: ChannelMembership): WorkspaceCache {
  if (cache.memberships[ms.channelId] === undefined) return cache;
  return { ...cache, memberships: { ...cache.memberships, [ms.channelId]: ms } };
}

/** Where one of my sidebar items lives; an item with neither folder nor rank is back in its default place. */
export function setSidebarItem(cache: WorkspaceCache, item: SidebarItem): WorkspaceCache {
  const before = cache.sidebarItems ?? {};
  const items = { ...before };
  if (item.folderId === undefined && item.rank === undefined) {
    if (before[item.id] === undefined) return cache;
    delete items[item.id];
  } else items[item.id] = item;
  const next: WorkspaceCache = { ...cache };
  if (Object.keys(items).length > 0) next.sidebarItems = items;
  else delete next.sidebarItems;
  return next;
}

export function upsertSidebarFolder(cache: WorkspaceCache, folder: SidebarFolder): WorkspaceCache {
  return { ...cache, sidebarFolders: { ...cache.sidebarFolders, [folder.id]: folder } };
}

/** Deletes one of my folders; its items go back to their default section or the page tree, in default order. */
export function removeSidebarFolder(cache: WorkspaceCache, folderId: string): WorkspaceCache {
  const folders: Record<string, SidebarFolder> = { ...cache.sidebarFolders };
  const items: Record<string, SidebarItem> = { ...cache.sidebarItems };
  let changed = folders[folderId] !== undefined;
  delete folders[folderId];
  for (const id of Object.keys(items)) {
    if (items[id]!.folderId !== folderId) continue;
    delete items[id];
    changed = true;
  }
  if (!changed) return cache;
  const next: WorkspaceCache = { ...cache };
  if (Object.keys(folders).length > 0) next.sidebarFolders = folders;
  else delete next.sidebarFolders;
  if (Object.keys(items).length > 0) next.sidebarItems = items;
  else delete next.sidebarItems;
  return next;
}

/**
 * A `member.updated` payload never carries the email: keep the one the cache already had (bootstrap), so an event
 * doesn't erase it. Phones are dropped with the event (the member may have made theirs private); profile settings
 * read their own from GET profile.
 */
export function mergeMemberEvent(prev: Member | undefined, next: Member): Member {
  if (prev === undefined || prev.email === undefined || next.email !== undefined) return next;
  return { ...next, email: prev.email };
}

// ---------- Selectors ----------

export type UnreadState = { unread: boolean; mentions: number };

/** unread = newer channel-level messages than my read marker (false when muted); mentions always count. */
export function channelUnread(cache: WorkspaceCache, channelId: string): UnreadState {
  const ms = cache.memberships[channelId];
  const c = cache.channels[channelId];
  if (ms === undefined || c === undefined) return { unread: false, mentions: 0 };
  return { unread: !ms.muted && c.lastMessageSeq > ms.lastReadSeq, mentions: ms.mentionCount };
}

/** App badge: unread mentions (incl. DMs) across my non-archived channels. */
export function totalBadge(cache: WorkspaceCache): number {
  let n = 0;
  for (const id of Object.keys(cache.memberships)) {
    const c = cache.channels[id];
    if (c !== undefined && !c.archived) n += cache.memberships[id]!.mentionCount;
  }
  return n;
}

/**
 * Any conversation with unread messages (Slack's bold rows; the tab title's "* "): my non-archived, unmuted
 * conversations past their read marker. Mentions count separately (totalBadge).
 */
export function hasUnread(cache: WorkspaceCache): boolean {
  for (const id of Object.keys(cache.memberships)) {
    const c = cache.channels[id];
    if (c !== undefined && !c.archived && channelUnread(cache, id).unread) return true;
  }
  return false;
}

/** The 1:1 DM channel between me and `userId` (or my self-DM when userId is me). */
export function dmChannelWith(cache: WorkspaceCache, userId: string): Channel | undefined {
  for (const id of Object.keys(cache.channels)) {
    const c = cache.channels[id]!;
    if (c.kind !== "dm" || c.dmUserIds === undefined) continue;
    const others = c.dmUserIds.filter((u) => u !== cache.meId);
    if (userId === cache.meId ? others.length === 0 : others.length === 1 && others[0] === userId) return c;
  }
  return undefined;
}

/**
 * Channels I share with `userId`, as far as the client knows: DMs/group DMs containing both, plus my other
 * channels (the server only shows restricted members people they share a channel with).
 */
export function sharedChannelIdsWith(cache: WorkspaceCache, userId: string): string[] {
  const out: string[] = [];
  for (const id of Object.keys(cache.memberships)) {
    const c = cache.channels[id];
    if (c === undefined) continue;
    if (c.kind === "dm" || c.kind === "group_dm") {
      if (c.dmUserIds !== undefined && c.dmUserIds.includes(userId)) out.push(id);
    } else out.push(id);
  }
  return out.toSorted();
}

export function memberName(cache: WorkspaceCache, userId: string): string {
  const m = cache.members[userId];
  return m === undefined ? userId : m.displayName;
}
